import { BURNABY_PHARMACY_ID, MANITOBA_PHARMACY_ID } from '../utils/pharmacyDeliveryTypes';

/**
 * Pharmacy-level UI feature flags — the single source of truth for per-site
 * frontend behaviour that isn't (yet) backed by a database configuration row.
 *
 * Keep every pharmacy-scoped UI toggle here so "which sites get feature X" is
 * answerable in ONE place, instead of scattering `pharmacyId === '...'` checks
 * across components (the one-source-of-truth rule in CLAUDE.md). Other
 * pharmacy-level configuration already lives together the same way:
 *   - delivery-type restrictions → `utils/pharmacyDeliveryTypes.ts`
 *   - backend-backed feature flags (ShipStation / Descartes) → `services/pharmacyFeatures.ts`
 *
 * When a flag graduates to something a pharmacy admin should toggle at runtime,
 * migrate it onto the backend `pharmacy_configurations` row and read it through
 * `services/pharmacyFeatures.ts` instead of hard-coding it here.
 */
export interface PharmacyFeatureFlags {
  /**
   * Show the prescriber's free-text Kroll comment (Rx.DocCom / `doctorComment`)
   * as the amber "Prescriber Note" block on the hardcopy.
   *
   * Burnaby-only feature (GP-1020): Alberta prescribers dispensing into BC are
   * issued a pseudo BC licence, so their REAL out-of-province licence
   * (e.g. "AB #044452") is recorded in Kroll's DocCom and must print on the
   * hardcopy. At every other site DocCom holds unrelated free text — assistant
   * names, email preferences, 5–6 line paragraphs — that should NOT print. So
   * this defaults OFF and is enabled only for Burnaby, stopping the note from
   * leaking onto every site's hardcopies.
   */
  showPrescriberNote: boolean;

  /**
   * Print the total charge for the fill ("Rx Total: $x.xx") on the vial label.
   *
   * Manitoba-only feature (GP-869): the College of Pharmacists of Manitoba
   * requires the prescription total to appear on the vial label. No other
   * province's College asks for it and the label is already dense, so this
   * defaults OFF and is enabled only for the Winnipeg site.
   */
  showRxTotalOnVialLabel: boolean;
}

/** Baseline flags applied to every pharmacy unless overridden below. */
const DEFAULT_FLAGS: PharmacyFeatureFlags = {
  showPrescriberNote: false,
  showRxTotalOnVialLabel: false,
};

/** Per-pharmacy overrides, keyed by pharmacy id. */
const PHARMACY_FEATURE_FLAGS: Readonly<
  Record<string, Partial<PharmacyFeatureFlags>>
> = {
  [BURNABY_PHARMACY_ID]: {
    showPrescriberNote: true,
  },
  [MANITOBA_PHARMACY_ID]: {
    showRxTotalOnVialLabel: true,
  },
};

/**
 * Resolve the full flag set for a pharmacy (defaults merged with any overrides).
 * An unknown / missing pharmacy id yields the safe defaults — the Burnaby-only
 * features stay off rather than leaking to other sites.
 */
export function getPharmacyFeatureFlags(
  pharmacyId: string | null | undefined,
): PharmacyFeatureFlags {
  const overrides = pharmacyId ? PHARMACY_FEATURE_FLAGS[pharmacyId] : undefined;
  return { ...DEFAULT_FLAGS, ...overrides };
}

/** Convenience helper: is the hardcopy "Prescriber Note" block enabled here? */
export function isPrescriberNoteEnabled(
  pharmacyId: string | null | undefined,
): boolean {
  return getPharmacyFeatureFlags(pharmacyId).showPrescriberNote;
}

/** Convenience helper: does this site's vial label carry the Rx total? (GP-869) */
export function isRxTotalOnVialLabelEnabled(
  pharmacyId: string | null | undefined,
): boolean {
  return getPharmacyFeatureFlags(pharmacyId).showRxTotalOnVialLabel;
}
